import React, { useState, useRef, useEffect } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel } from
'@mui/material';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../services/api';
import SearchableDataTable from '../components/SearchableDataTable';

const PointInTimeManagement = () => {
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [pointInTimes, setPointInTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPointInTime, setCurrentPointInTime] = useState({
    Name: ''
  });
  const [creationType, setCreationType] = useState('fresh'); // 'fresh' or 'copy'
  const [templatePointInTime, setTemplatePointInTime] = useState('');
  const [nameError, setNameError] = useState('');

  // State for import dialog
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // State for row selection
  const [selectedRows, setSelectedRows] = useState([]);

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  // Validate name uniqueness
  const validateNameUniqueness = (name) => {
    if (!name.trim()) {
      setNameError('');
      return true;
    }

    const isDuplicate = pointInTimes.some((pit) =>
    pit.Name.toLowerCase() === name.trim().toLowerCase() && (
    !editMode || pit.id !== currentPointInTime.id)
    );

    if (isDuplicate) {
      setNameError('A point in time with this name already exists');
      return false;
    } else {
      setNameError('');
      return true;
    }
  };

  // Load point in times when component mounts
  useEffect(() => {
    loadPointInTimes();
  }, []);

  const loadPointInTimes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/point-in-times');

      // Transform API data to match frontend schema
      const transformedPointInTimes = (response.data.data || []).map((pointInTime) => ({
        id: pointInTime.id,
        Code: pointInTime.Code,
        Name: pointInTime.Name,
        ModifiedBy: pointInTime.ModifiedBy,
        ModifiedOn: new Date(pointInTime.ModifiedOn).toLocaleString(),
        dateCreated: new Date(pointInTime.dateCreated).toLocaleString()
      }));
      setPointInTimes(transformedPointInTimes);
    } catch (error) {
      console.error('Error loading point in times:', error);
      showNotification('Error loading point in times', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle adding a new point in time
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentPointInTime({
      Name: ''
    });
    setCreationType('fresh');
    setTemplatePointInTime('');
    setNameError('');
    setOpenDialog(true);
  };

  // Handle editing a point in time
  const handleEdit = (pointInTime) => {
    setEditMode(true);
    setCurrentPointInTime({
      id: pointInTime.id,
      Code: pointInTime.Code, // Keep code for display but don't allow editing
      Name: pointInTime.Name
    });
    setNameError('');
    setOpenDialog(true);
  };

  // Handle saving a point in time
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!currentPointInTime.Name) {
        showNotification('Name is required', 'error');
        return;
      }

      // Validate name uniqueness
      if (!validateNameUniqueness(currentPointInTime.Name)) {
        return;
      }

      if (!editMode && creationType === 'copy' && !templatePointInTime) {
        showNotification('Please select a point in time to copy from', 'error');
        return;
      }

      const pointInTimeData = {
        Name: currentPointInTime.Name,
        ModifiedBy: 'admin'
      };

      if (editMode) {
        // Update existing point in time
        await api.put(`/point-in-times/${currentPointInTime.id}`, pointInTimeData);
        showNotification('Point in time updated successfully', 'success');
      } else {
        // Create new point in time
        const response = await api.post('/point-in-times', pointInTimeData);
        const newPointInTime = response.data.data;

        // If copying, duplicate data from template
        if (creationType === 'copy' && templatePointInTime && newPointInTime) {
          await handleCopyPointInTimeData(templatePointInTime, newPointInTime.Code);
        }

        showNotification('Point in time created successfully', 'success');
      }

      await loadPointInTimes();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving point in time:', error);
      const errorMessage = error.response?.data?.message || 'Error saving point in time';
      showNotification(errorMessage, 'error');
    }
  };

  // Handle copying data from template point in time
  const handleCopyPointInTimeData = async (fromPointInTimeCode, toPointInTimeCode) => {
    try {


      // Copy scenarios
      const scenariosResponse = await api.get('/master-scenarios');


      const allScenarios = scenariosResponse.data.data || [];



      const templateScenarios = allScenarios.filter(
        (scenario) => {

          return scenario.PointInTime === fromPointInTimeCode;
        }
      );



      if (templateScenarios.length === 0) {

        showNotification(`No scenarios found for point in time ${fromPointInTimeCode}`, 'info');
      }

      for (const scenario of templateScenarios) {

        const newScenarioData = {
          ...scenario,
          PointInTime: toPointInTimeCode,
          ScenarioCode: `${scenario.ScenarioCode}_COPY_${Date.now()}`, // Make unique
          ModifiedBy: 'admin',
          entityList: [] // Start with empty entity list for new point in time
        };
        delete newScenarioData.id;
        delete newScenarioData.ModifiedOn;
        delete newScenarioData.dateCreated;


        const createResponse = await api.post('/master-scenarios', newScenarioData);

      }

      // Copy entities
      const entitiesResponse = await api.get('/entity-models/master');
      const templateEntities = entitiesResponse.data.data?.filter(
        (entity) => entity.PointInTime === fromPointInTimeCode
      ) || [];

      for (const entity of templateEntities) {
        const newEntityData = {
          ...entity,
          PointInTime: toPointInTimeCode,
          PointInTimeCode: toPointInTimeCode,
          ModifiedBy: 'admin'
        };
        delete newEntityData.id;
        delete newEntityData.ModifiedOn;
        delete newEntityData.dateCreated;

        await api.post('/entity-models/master', newEntityData);
      }

      // Copy states
      const statesResponse = await api.get('/states/master');
      const templateStates = statesResponse.data.data?.filter(
        (state) => state.PointInTime === fromPointInTimeCode
      ) || [];

      for (const state of templateStates) {
        const newStateData = {
          ...state,
          PointInTime: toPointInTimeCode,
          ModifiedBy: 'admin'
        };
        delete newStateData.id;
        delete newStateData.ModifiedOn;
        delete newStateData.dateCreated;

        await api.post('/states/master', newStateData);
      }

      // Copy adjustments
      const adjustmentsResponse = await api.get('/adjustments/master');
      const templateAdjustments = adjustmentsResponse.data.data?.filter(
        (adjustment) => adjustment.PointInTime === fromPointInTimeCode
      ) || [];

      for (const adjustment of templateAdjustments) {
        const newAdjustmentData = {
          ...adjustment,
          PointInTime: toPointInTimeCode,
          ModifiedBy: 'admin'
        };
        delete newAdjustmentData.id;
        delete newAdjustmentData.ModifiedOn;
        delete newAdjustmentData.dateCreated;

        await api.post('/adjustments/master', newAdjustmentData);
      }

      showNotification('Data copied successfully from template point in time', 'success');
    } catch (error) {
      console.error('Error copying point in time data:', error);
      showNotification('Warning: Point in time created but some data may not have been copied', 'warning');
    }
  };

  // Handle deleting all data associated with a point in time
  const handleDeletePointInTimeData = async (pointInTimeCode) => {
    try {


      // Delete scenarios
      const scenariosResponse = await api.get('/master-scenarios');
      const scenariosToDelete = scenariosResponse.data.data?.filter(
        (scenario) => scenario.PointInTime === pointInTimeCode
      ) || [];

      for (const scenario of scenariosToDelete) {

        await api.delete(`/master-scenarios/${scenario.id}`);
      }

      // Delete entities
      const entitiesResponse = await api.get('/entity-models/master');
      const entitiesToDelete = entitiesResponse.data.data?.filter(
        (entity) => entity.PointInTime === pointInTimeCode
      ) || [];

      for (const entity of entitiesToDelete) {

        await api.delete(`/entity-models/master/${entity.id}`);
      }

      // Delete states
      const statesResponse = await api.get('/states/master');
      const statesToDelete = statesResponse.data.data?.filter(
        (state) => state.PointInTime === pointInTimeCode
      ) || [];

      for (const state of statesToDelete) {

        await api.delete(`/states/master/${state.id}`);
      }

      // Delete adjustments
      const adjustmentsResponse = await api.get('/adjustments/master');
      const adjustmentsToDelete = adjustmentsResponse.data.data?.filter(
        (adjustment) => adjustment.PointInTime === pointInTimeCode
      ) || [];

      for (const adjustment of adjustmentsToDelete) {

        await api.delete(`/adjustments/master/${adjustment.id}`);
      }


    } catch (error) {
      console.error('Error deleting point in time data:', error);
      throw error; // Re-throw to be handled by calling function
    }
  };

  // Handle deleting selected point in times
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected point in times and all their associated data (scenarios, entities, states, adjustments)?`)) {
      setDeleting(true);
      try {
        // Delete associated data for each point in time
        for (const id of selectedRows) {
          const pointInTime = pointInTimes.find((pit) => pit.id === id);
          if (pointInTime) {
            await handleDeletePointInTimeData(pointInTime.Code);
          }
        }

        // Delete the point in times themselves
        const deletePromises = selectedRows.map((id) => api.delete(`/point-in-times/${id}`));
        await Promise.all(deletePromises);

        // Refresh the point in times list
        await loadPointInTimes();

        setSelectedRows([]);
        showNotification(`Successfully deleted ${selectedRows.length} point in times and all associated data`, 'success');
      } catch (error) {
        console.error('Error deleting point in times:', error);
        showNotification('Failed to delete point in times', 'error');
      } finally {
        setDeleting(false);
      }
    }
  };

  // Handle deleting a single point in time
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this point in time and all its associated data (scenarios, entities, states, adjustments)?')) {
      try {
        // Find the point in time to get its code
        const pointInTime = pointInTimes.find((pit) => pit.id === id);
        if (pointInTime) {
          // Delete all associated data first
          await handleDeletePointInTimeData(pointInTime.Code);
        }

        // Delete the point in time itself
        await api.delete(`/point-in-times/${id}`);

        // Refresh the point in times list
        await loadPointInTimes();

        showNotification('Point in time and all associated data deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting point in time:', error);
        showNotification('Failed to delete point in time', 'error');
      }
    }
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Handle selection change
  const handleSelectionChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing dialogs
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setConfirmCloseOpen(false);
  };

  // Handle export to Excel
  const exportToExcel = () => {
    try {
      // Prepare data for export - only Code and Name columns
      const exportData = pointInTimes.map((pointInTime) => ({
        'Code': pointInTime.Code,
        'Name': pointInTime.Name
      }));

      // Create workbook and worksheet
      // Always include headers even if no data by specifying header option
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData, {
        header: ['Code', 'Name']
      });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Point In Times');

      // Generate Excel file and save
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      saveAs(data, `point_in_times_${new Date().toISOString().split('T')[0]}.xlsx`);
      showNotification('Point in times exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting point in times:', error);
      showNotification('Failed to export point in times', 'error');
    }
  };

  // Handle import button click
  const handleImport = () => {
    fileInputRef.current.click();
  };

  // Handle file selection for import
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          throw new Error('The Excel file is empty');
        }

        // Get all column names from the first row, case-insensitive
        const fileColumns = Object.keys(jsonData[0] || {});
        const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

        // Map frontend column names to possible Excel column names
        const columnMappings = {
          'Code': ['code', 'point in time code', 'pit code'],
          'Name': ['name', 'point in time name', 'pit name']
        };

        // Find matching columns in the Excel file
        const matchedColumns = {};
        let missingColumns = [];

        for (const [field, possibleNames] of Object.entries(columnMappings)) {
          const found = fileColumnsLower.some((col, index) => {
            if (possibleNames.includes(col.toLowerCase())) {
              matchedColumns[field] = fileColumns[index];
              return true;
            }
            return false;
          });

          // Only Name is required - Code is auto-generated
          if (!found && ['Name'].includes(field)) {
            missingColumns.push(possibleNames[0]);
          }
        }

        if (missingColumns.length > 0) {
          throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
        }

        // Get all existing point in times to check for duplicates
        const allPointInTimesResponse = await api.get('/point-in-times');
        const existingPointInTimes = allPointInTimesResponse.data.data || [];
        const pointInTimesMap = new Map(
          existingPointInTimes.map((p) => [p.Code.toLowerCase(), p])
        );

        // Process each row and track results
        const importResults = {
          created: 0,
          updated: 0,
          errors: []
        };

        // Process rows sequentially to avoid race conditions
        for (let i = 0; i < jsonData.length; i++) {
          try {
            const row = jsonData[i];

            // Extract values using matched column names
            const name = String(row[matchedColumns.Name] || '').trim();
            const code = matchedColumns.Code ? String(row[matchedColumns.Code] || '').trim() : '';

            // Validate required fields
            if (!name) {
              throw new Error('Name is required');
            }

            const pointInTimeData = {
              Name: name,
              ModifiedBy: 'admin'
            };

            // Check for existing point in time by Code (if provided) or Name
            let existingPointInTime = null;

            if (code) {
              // If code is provided, check by code first
              existingPointInTime = pointInTimesMap.get(code.toLowerCase());
            }

            if (!existingPointInTime) {
              // If not found by code or no code provided, check by name
              existingPointInTime = Array.from(pointInTimesMap.values()).find((p) =>
              p.Name.toLowerCase() === name.toLowerCase()
              );
            }

            if (existingPointInTime) {
              // Update existing point in time
              await api.put(`/point-in-times/${existingPointInTime.id}`, pointInTimeData);
              importResults.updated++;
            } else {
              // Create new point in time (Code will be auto-generated by backend)
              await api.post('/point-in-times', pointInTimeData);
              importResults.created++;
            }

          } catch (error) {
            console.error(`Error processing row ${i + 1}:`, jsonData[i], error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            importResults.errors.push(`Row ${i + 1}: ${errorMessage}`);
          }
        }

        // Check if there were any errors
        if (importResults.errors.length > 0) {
          const errorMessage = importResults.errors.length === jsonData.length ?
          'All rows had errors. No data was imported.' :
          `Completed with ${importResults.errors.length} error(s) out of ${jsonData.length} rows.`;

          if (importResults.created === 0 && importResults.updated === 0) {
            throw new Error(`Import failed: ${errorMessage}\n\n${importResults.errors.join('\n')}`);
          } else {
            // Some rows succeeded, show warning
            showNotification(`Partial import: ${importResults.created} created, ${importResults.updated} updated, ${importResults.errors.length} errors`, 'warning');
          }
        }

        // Refresh data
        await loadPointInTimes();

        // Only show success message if no errors were shown before
        if (importResults.errors.length === 0) {
          const updatedMsg = importResults.updated > 0 ? `, updated ${importResults.updated}` : '';
          showNotification(`Successfully imported ${importResults.created} point in time${importResults.created !== 1 ? 's' : ''}${updatedMsg}`, 'success');
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error importing point in times:', error);
      showNotification(error.message || 'Failed to import point in times', 'error');
    } finally {
      setImporting(false);
      // Clear the file input
      if (event.target) {
        event.target.value = null;
      }
    }
  };

  // Define columns for the data table - showing only Code and Name
  const columns = [
  { field: 'Code', headerName: 'CODE', width: 200, sortable: true },
  { field: 'Name', headerName: 'NAME', flex: 1, sortable: true },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    renderCell: (params) =>
    <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
        size="small"
        onClick={() => handleEdit(params.row)}
        title="Edit Point In Time">

            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton
        size="small"
        onClick={() => handleDelete(params.row.id)}
        title="Delete Point In Time">

            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

  }];


  return (
    <Box className="scenario-management-container">
      {/* Main Content */}
      <Box className="main-content">
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>
              Manage Point In Time
            </Typography>
            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
              {selectedRows.length > 0 ? `${selectedRows.length} selected` : ''}
            </Typography>
          </Box>
          
          {/* Action Buttons */}
          <Box className="button-group">
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={pointInTimes.length === 0}>

              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={importing ? <CircularProgress size={20} /> : <UploadIcon />}
              onClick={handleImport}
              disabled={importing}>

              {importing ? 'Importing...' : 'Import'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={<AddIcon />}
              onClick={handleAddNew}>

              Add
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0 || deleting}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4
                }
              }}>

              {deleting ? 'Deleting...' : 'Remove'}
            </Button>
          </Box>
        </Box>

        {/* Data Table */}
        <Box className="table-container">
          {loading ?
          <LinearProgress /> :

          <SearchableDataTable
            searchPlaceholder="Search Point In Times..."
            rows={pointInTimes}
            columns={columns}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSelectionModelChange={handleSelectionChange}
            selectionModel={selectedRows}
            checkboxSelection={true}
            disableSelectionOnClick={true}
            height="100%" />

          }
        </Box>
      </Box>
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".xlsx,.xls"
        style={{ display: 'none' }} />


      {/* Add/Edit Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleCloseDialog}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog open={openDialog} onClose={handleRequestClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Point In Time' : 'Add New Point In Time'}
        </DialogTitle>
        <DialogContent>
          {editMode &&
          <TextField
            margin="dense"
            label="Code"
            fullWidth
            autoComplete="off"
            variant="outlined"
            value={currentPointInTime.Code || ''}
            disabled
            sx={{ mb: 2, '& .MuiInputBase-input.Mui-disabled': { color: 'text.secondary' } }}
            helperText="Code is auto-generated and cannot be changed" />

          }
          
          {!editMode &&
          <Box sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ mb: 2 }}>Creation Type</FormLabel>
              <RadioGroup
              value={creationType}
              onChange={(e) => setCreationType(e.target.value)}
              row>

                <FormControlLabel value="fresh" control={<Radio />} label="Create Fresh" />
                <FormControlLabel value="copy" control={<Radio />} label="Copy from Existing" />
              </RadioGroup>
            </Box>
          }
          
          {!editMode && creationType === 'copy' &&
          <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Template Point In Time *</InputLabel>
              <Select
              value={templatePointInTime}
              label="Template Point In Time *"
              onChange={(e) => setTemplatePointInTime(e.target.value)}>

                {pointInTimes.map((pit) =>
              <MenuItem key={pit.id} value={pit.Code}>
                    {pit.Code} - {pit.Name}
                  </MenuItem>
              )}
              </Select>
            </FormControl>
          }
          
          <TextField
            autoFocus
            margin="dense"
            label="Name *"
            autoComplete="off"
            fullWidth
            variant="outlined"
            value={currentPointInTime.Name}
            onChange={(e) => {
              const newName = e.target.value;
              setCurrentPointInTime({
                ...currentPointInTime,
                Name: newName
              });
              validateNameUniqueness(newName);
            }}
            error={!!nameError}
            helperText={nameError}
            sx={{ mb: 2 }} />

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!!nameError || !currentPointInTime.Name.trim()}>

            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>

        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>);

};

export default PointInTimeManagement;
